import { useGoogleLogin } from "@react-oauth/google";
import { Formik } from "formik";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { debounce } from "lodash";
import { Button, Input, TextView } from "../../../components/index.js";
import {
  useFacebook,
  useGoogleToken,
  useSignup,
  useResendVerificationLink,
} from "../../../services/mutations.js";
import { ASSETS, PATH } from "../../../utils/config.js";
import * as Styled from "../styles.js";
import BetaAmbassadorModal from "../../../components/BetaAmbassadorModal/index.jsx";
import { colors } from "../../../utils/theme.js";
import FacebookLogin from "@greatsumini/react-facebook-login";
import AuthWrapper from "../../../components/AuthWrapper";
import SignUpPopup from "../../../components/SignUpPopup";
import CustomPhoneInput from "../../../components/PhoneInput/index.jsx";
import { isValidPhoneNumber } from "react-phone-number-input";
import OTPModal from "../../../components/PasswordAlert/index.js";
import Icon from "assets/images/verify-message.svg";
import CalendarIcon from "assets/images/calendar.svg";
import { Separator } from "../../../components/PasswordAlert/styles.js";
import { SignupSubscriptionModal } from "../../../components/SignupSubscriptionModal/index.jsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { subYears } from "date-fns";
import {
  Checkbox,
  FormControlLabel,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Box,
} from "@mui/material";
import { Country, State } from "country-state-city";
import WheelPlaceholder from "@assets/images/wheelPlaceholder.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IyugaTooltip from "../../../components/IyugaTooltip";

import { TermsAndConditionsAPI } from "../../../components/Settings/LegalTabs/TermsAndConditionsAPI.jsx";
import { PrivacyPolicyAPI } from "../../../components/Settings/LegalTabs/PrivacyPolicyAPI.jsx";
import { useGetTermsAndConditions } from "../../../services/queries.js";
import { checkReferral, getProfileByReferCode } from "../../../services/api.js";
import { useDispatch } from "react-redux";
import {
  openDialogPopup,
  closeDialogPopup,
} from "../../../redux/reducers/dialogPopup.js";
import CustomLoader from "../../../sharedcomponents/CustomLoader.js";
import EmailVerificationPopup from "../../../components/EmailVerificationPopup";

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  "&.Mui-checked": {
    color: "#9B5AF8",
  },
}));

const StyledTermsContent = styled("div")(({ theme }) => ({
  height: "90%",
  overflow: "auto",
  borderBottom: "1px solid #E1E1E1",
  paddingBottom: "16px",
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "16px 0px 0px 0px",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  flexDirection: "column",
  paddingLeft: "16px",
  paddingRight: "16px",
}));

export const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "end",
  alignItems: "center",
  gap: "12px",
}));

export const CancelButton = styled("div")(({ theme }) => ({
  cursor: "pointer",
  fontFamily: "DM Sans",
  color: "#495057",
  fontSize: "16px",
  lineHeight: "20px",
}));

export const HeaderIcon = styled("img")(({ theme }) => ({
  width: "40px",
  height: "40px",
}));

const StyledSuccessText = styled(TextView)(({ theme }) => ({
  color: "#212529",
  fontSize: "12px",
  marginTop: "4px",
  fontWeight: "200",
  fontStyle: "italic",
}));

// ReCAPTCHA Site Key - Get from environment variable
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
// ReCAPTCHA Feature Flag - Controls whether reCAPTCHA is enabled
const RECAPTCHA_ENABLED = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

// Creating schema
const schema = Yup.object().shape({
  email: Yup.string()
    .required("Email is a Required field")
    .email("Please Enter a Valid Email"),
  firstName: Yup.string().required("First name is a Required field"),
  username: Yup.string().max(30, "Profile Name must be at most 30 characters"),
  lastName: Yup.string().required("Last name is a Required field"),
  phone: Yup.string()
    .required("Phone number is a Required field")
    .test("is-valid-phone", "Please enter a valid phone number", (value) =>
      isValidPhoneNumber(value)
    ),
  password: Yup.string()
    .required("Password is a Required field")
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+|{}[\]:";'<>?,./])/,
      "Password must contain at least one uppercase letter, one numeric and one special character"
    ),
  confirmPassword: Yup.string()
    .required("Password is a Required field")
    .oneOf([Yup.ref("password"), null], "Passwords must match"),
  dob: Yup.date()
    .nullable()
    .typeError("Date of Birth is required")
    .required("Date of Birth is required")
    .max(subYears(new Date(), 18), "You must be at least 18 years old"),
  termsAndConditions: Yup.boolean()
    .oneOf([true], "You must accept the Terms and Conditions")
    .required("You must accept the Terms and Conditions"),
  country: Yup.string().required("Country is a Required field"),
  state: Yup.string().required("State/Province is a Required field"),
});

const Signup = () => {
  // Call the API for terms and conditions data
  const {
    data: termsData,
    isLoading: termsLoading,
    error: termsError,
  } = useGetTermsAndConditions();

  const recaptchaRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [termsPopup, setTermsPopup] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isReferralLoading, setIsReferralLoading] = useState(false);
  const [referralProfile, setReferralProfile] = useState(null);
  const [referralError, setReferralError] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [showOtherReferralModal, setShowOtherReferralModal] = useState(false);
  const [previousReferrer, setPreviousReferrer] = useState("");
  const [emailErrors, setEmailErrors] = useState([]);
  const [usernameErrors, setUsernameErrors] = useState([]);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  const datePickerRef = useRef(null);

  const onClose = () => {
    setOpen(false);
  };

  const nav = useNavigate();
  let userLogin = useSignup();
  let getGoogleToken = useGoogleToken();
  let getFacebookToken = useFacebook();
  let resendVerification = useResendVerificationLink();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const queryReferralCode = searchParams.get("referral_code");
  const promoCode = searchParams.get("promo_code");

  const { referralCode: pathReferralCode } = useParams();
  const effectiveReferralCode = pathReferralCode || queryReferralCode;

  const [referralData, setReferralData] = useState(null);
  const [promoCodeValue, setPromoCodeValue] = useState(promoCode || "");
  const [showEmailVerificationPopup, setShowEmailVerificationPopup] =
    useState(false);

  const dispatch = useDispatch();

  const loginWithFacebook = (response) => {
    getFacebookToken.mutate(
      { access_token: response.accessToken },
      {
        onSuccess: (res) => {
          console.log("Facebook Login", res);
          nav("/gallery");
        },
        onError: (err) => {
          console.log("Facebook login failed", err);
        },
      }
    );
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      getGoogleToken.mutate(
        { access_token: codeResponse.access_token },
        {
          onSuccess: (res) => {
            console.log("Google Login", res);
            nav("/gallery");
          },
          onError: (err) => {
            console.log("Google login failed", err);
          },
        }
      );
    },
  });

  const handleReferralCodeChange = useCallback(
    debounce(async (code) => {
      if (!code || code.length < 3) {
        setReferralProfile(null);
        setReferralError(null);
        return;
      }

      setIsReferralLoading(true);
      setReferralError(null);
      try {
        const response = await getProfileByReferCode(code);
        setReferralProfile(response.data.data);
      } catch (error) {
        setReferralProfile(null);
        setReferralError(
          "The code you have entered is invalid. Please try again."
        );
      } finally {
        setIsReferralLoading(false);
      }
    }, 1500),
    []
  );

  const handleSubmit = async (values, formikHelpers) => {
    // Check if reCAPTCHA is enabled via feature flag
    if (RECAPTCHA_ENABLED) {
      // Check if reCAPTCHA is configured
      if (!RECAPTCHA_SITE_KEY) {
        console.warn(
          "reCAPTCHA site key not configured. Proceeding without reCAPTCHA verification."
        );
        // Continue without reCAPTCHA for development/testing
      } else if (!values.recaptchaToken) {
        // Check if recaptcha token exists
        console.log("No reCAPTCHA token, triggering reCAPTCHA...");
        // Trigger the invisible reCAPTCHA
        setPendingFormValues({ values, formikHelpers });
        if (recaptchaRef.current) {
          console.log("Executing reCAPTCHA...");
          recaptchaRef.current.execute();
        } else {
          console.error("reCAPTCHA ref is null!");
          toast.error("reCAPTCHA not loaded. Please refresh the page.");
        }
        return;
      }
    }

    try {
      const res = await checkReferral({
        email: values.email,
        referral_code: values.referral_code,
      });

      if (res.data.data.exists) {
        setPreviousReferrer(res.data.data.referrer?.name);
        setShowOtherReferralModal(true);
      } else {
        handleSignupApiCallSubmit(values);
      }
    } catch (error) {
      // Check for email validation errors from checkReferral API response
      if (
        error.response?.data?.data &&
        error.response?.data?.data?.email &&
        error.response?.data?.data?.email.length > 0
      ) {
        setEmailErrors((prev) => {
          // Check if this email validation error is already in the array
          const existingIndex = prev.findIndex(
            (error) =>
              error.email === values.email && error.type === "validation"
          );
          if (existingIndex >= 0) {
            // Update existing validation error
            const updated = [...prev];
            updated[existingIndex] = {
              message: error.response.data.data.email[0], // Use first error message
              email: values.email,
              type: "validation",
            };
            return updated;
          } else {
            // Add new validation error
            return [
              ...prev,
              {
                message: error.response?.data?.data?.email[0], // Use first error message
                email: values.email,
                type: "validation",
              },
            ];
          }
        });
        // Reset reCAPTCHA on error
        if (RECAPTCHA_ENABLED && recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        if (RECAPTCHA_ENABLED && formikHelpers) {
          formikHelpers.setFieldValue("recaptchaToken", "");
        }
        return; // Don't proceed to signup if there are validation errors
      }
      console.error("Error checking referral:", error);
      // Reset reCAPTCHA on error
      if (RECAPTCHA_ENABLED && recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      if (RECAPTCHA_ENABLED && formikHelpers) {
        formikHelpers.setFieldValue("recaptchaToken", "");
      }
    }
  };

  const onRecaptchaChange = (token) => {
    console.log("onRecaptchaChange called with token:", token);
    console.log("pendingFormValues:", pendingFormValues);

    if (token && pendingFormValues) {
      const { values, formikHelpers } = pendingFormValues;
      console.log(
        "Setting recaptchaToken in formik and proceeding with signup..."
      );
      // Set the token in formik
      if (formikHelpers) {
        formikHelpers.setFieldValue("recaptchaToken", token);
      }
      // Now proceed with the actual signup
      const updatedValues = { ...values, recaptchaToken: token };
      handleSubmit(updatedValues, formikHelpers);
      setPendingFormValues(null);
    } else {
      console.warn(
        "reCAPTCHA verification failed - Token or pendingFormValues missing:",
        {
          token,
          pendingFormValues,
        }
      );
      // Clear pending form values to prevent bypass attempts
      setPendingFormValues(null);
      // Show error to user
      if (!token && pendingFormValues) {
        toast.error("reCAPTCHA verification failed. Please try again.");
        // Reset the reCAPTCHA widget
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
    }
  };

  const onRecaptchaExpired = () => {
    console.warn("reCAPTCHA expired - clearing pending form values");
    toast.error("reCAPTCHA expired. Please try again.");
    // Clear pending form values to prevent submission with expired token
    setPendingFormValues(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const onRecaptchaError = () => {
    console.error("reCAPTCHA error occurred - clearing pending form values");
    toast.error("reCAPTCHA error. Please try again.");
    // Clear pending form values to prevent submission after error
    setPendingFormValues(null);
    // Reset the reCAPTCHA widget
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleSignupApiCallSubmit = (values, resetForm) => {
    // Then proceed with signup
    setName(`${values.firstName} ${values.lastName}`);
    setEmail(values.email);
    setFormValues(values);

    // Build username: prefer entered profile name, otherwise derive from first+last
    const rawUsername =
      values.username && values.username.trim().length > 0
        ? values.username.trim()
        : `${
            values.firstName.charAt(0).toUpperCase() + values.firstName.slice(1)
          }${
            values.lastName.charAt(0).toUpperCase() + values.lastName.slice(1)
          }`;

    // Sanitize username: remove spaces and limit to 30 chars
    const sanitizedUsername = rawUsername.replace(/\s+/g, "").slice(0, 30);

    let stateName = values.state;
    try {
      if (values.state) {
        const statesForCountry = State.getStatesOfCountry(
          values.country || values.country_code || ""
        );
        const foundState = (statesForCountry || []).find(
          (s) =>
            s.isoCode === values.state ||
            s.name === values.state ||
            s.value === values.state
        );
        if (foundState) stateName = foundState.name;
      }
    } catch (e) {
      // ignore
    }

    const { recaptchaToken, ...valuesWithoutToken } = values;
    userLogin.mutate(
      {
        ...valuesWithoutToken,
        first_name: values.firstName,
        last_name: values.lastName,
        username: sanitizedUsername,
        state: stateName,
      },
      {
        onSuccess: (response) => {
          if (response.data.success && response.data.status_code === 200) {
            // Clear email and username errors when form is submitted successfully
            setEmailErrors([]);
            setUsernameErrors([]);
            setVerifyModal(true);
            // Reset reCAPTCHA after successful signup
            if (RECAPTCHA_ENABLED && recaptchaRef.current) {
              recaptchaRef.current.reset();
            }
          }
        },
        onError: (err) => {
          // Reset reCAPTCHA on error
          if (RECAPTCHA_ENABLED && recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          if (
            err.response.data?.data &&
            err.response.data.data?.is_email_verified === "False"
          ) {
            setShowEmailVerificationPopup(true);
            return;
          }
          if (
            err.response?.data?.status_code === 400 &&
            err.response?.data?.message ===
              "A user is already registered with this e-mail address."
          ) {
            setEmailErrors((prev) => {
              // Check if this email conflict error is already in the array
              const existingIndex = prev.findIndex(
                (error) =>
                  error.email === values.email && error.type === "conflict"
              );
              if (existingIndex >= 0) {
                // Update existing conflict error
                const updated = [...prev];
                updated[existingIndex] = {
                  message:
                    "A user is already registered with this e-mail address.",
                  email: values.email,
                  type: "conflict",
                };
                return updated;
              } else {
                // Add new conflict error
                return [
                  ...prev,
                  {
                    message:
                      "A user is already registered with this e-mail address.",
                    email: values.email,
                    type: "conflict",
                  },
                ];
              }
            });
            return;
          }

          // Check for username conflict errors
          if (
            err.response?.data?.status_code === 400 &&
            (err.response?.data?.message ===
              "A user with that username already exists." ||
              err.response?.data?.message === "This username is already taken.")
          ) {
            setUsernameErrors((prev) => {
              // Check if this username is already in the array
              const existingIndex = prev.findIndex(
                (error) => error.username === values.username
              );
              if (existingIndex >= 0) {
                // Update existing error
                const updated = [...prev];
                updated[existingIndex] = {
                  message: "A user with that username already exists.",
                  username: values.username,
                };
                return updated;
              } else {
                // Add new error
                return [
                  ...prev,
                  {
                    message: "A user with that username already exists.",
                    username: values.username,
                  },
                ];
              }
            });
            return;
          }

          if (err.response && err.response.data) {
            const errorMessages = [];

            if (err.response?.data?.data?.redirect_url) {
              setOpen(true);
            }
            // Check for email errors
            if (err.response.data.email) {
              errorMessages.push(err.response.data.email.join(" "));
            }
            // Check for username errors
            if (err.response.data.username) {
              errorMessages.push(err.response.data.username.join(" "));
            }
            // Show the first error as a toast message
            if (errorMessages.length > 0) {
              toast.error(errorMessages.join(" "));
            } else {
              if (err.response.data?.data?.referral_code) {
                toast.error("Invalid referral code");
              } else {
                const errorData = err.response?.data?.data;
                for (const key in errorData) {
                  const value = errorData[key];
                  if (Array.isArray(value)) {
                    toast.error(value[0] || "An error occurred during signup.");
                    return;
                  }
                }
              }
            }
          }
        },
      }
    );
  };

  const handleResendVerification = () => {
    resendVerification.mutate(
      {
        email: email,
      },
      {
        onSuccess: (response) => {
          toast.success("Verification email has been resent successfully");
        },
        onError: (err) => {
          toast.error(
            err.response?.data?.message ||
              "Failed to resend verification email. Please try again."
          );
        },
      }
    );
  };

  useEffect(() => {
    let storedReferralData = sessionStorage.getItem("referralData");
    if (storedReferralData) {
      const parsedData = JSON.parse(storedReferralData);
      setReferralData(parsedData);
      if (parsedData.referral_code) {
        handleReferralCodeChange(parsedData.referral_code);
      }
    } else if (effectiveReferralCode) {
      const newReferralData = {
        referral_code: effectiveReferralCode,
      };
      setReferralData(newReferralData);
      sessionStorage.setItem("referralData", JSON.stringify(newReferralData));
      handleReferralCodeChange(effectiveReferralCode);
    }

    return () => {
      sessionStorage.removeItem("referralData");
    };
  }, [location.pathname, effectiveReferralCode, handleReferralCodeChange]);

  useEffect(() => {
    const storedPromoCode = localStorage.getItem("promoCode");

    if (promoCode) {
      // Always update the promo code if a new one is provided in URL
      localStorage.setItem("promoCode", promoCode);
      setPromoCodeValue(promoCode);
    } else if (storedPromoCode) {
      setPromoCodeValue(storedPromoCode);
    }
  }, [promoCode]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem("referralData");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <AuthWrapper style={{ justifyContent: "flex-start" }} title="Get Started!">
      <>
        <Formik
          validationSchema={schema}
          validateOnMount={true}
          initialValues={{
            username: "",
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            country: "US",
            state: "",
            country_code: "",
            phone_number: "",
            password: "",
            confirmPassword: "",
            referral_code: referralData?.referral_code || "",
            promo_code: promoCodeValue,
            checked: false,
            dob: null,
            termsAndConditions: false,
            recaptchaToken: "",
          }}
          enableReinitialize
          onSubmit={(values, formikHelpers) =>
            handleSubmit(values, formikHelpers)
          }
        >
          {({
            values,
            errors,
            touched,
            setTouched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setValues,
            isValid,
            resetForm,
          }) => (
            <>
              <Dialog
                open={showOtherReferralModal}
                onClose={() => setShowOtherReferralModal(false)}
                fullWidth={true}
              >
                <StyledDialogContent>
                  <ArrowBackIcon
                    onClick={() => setShowOtherReferralModal(false)}
                    style={{ cursor: "pointer", marginBottom: "-20px" }}
                  />
                  <img
                    src={WheelPlaceholder}
                    alt="iyuga"
                    width={80}
                    height={80}
                    style={{ alignSelf: "center" }}
                  />
                  <TextView
                    variant="Header3"
                    color={"#123051"}
                    style={{ textAlign: "center" }}
                  >
                    Hey there!
                  </TextView>
                  <TextView
                    variant="Body1"
                    color={"#565556"}
                    style={{ textAlign: "center" }}
                  >
                    We noticed that{" "}
                    <span style={{ fontWeight: "bold" }}>{values.email}</span>{" "}
                    is already connected to{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {previousReferrer}
                    </span>{" "}
                    through our Earnings Program.
                  </TextView>
                  <Button
                    style={{
                      width: "auto",
                      alignSelf: "center",
                      padding: "10px",
                    }}
                    onClick={() => {
                      handleSignupApiCallSubmit(values);
                      setShowOtherReferralModal(false);
                    }}
                  >
                    Continue
                  </Button>
                </StyledDialogContent>
              </Dialog>
              <form noValidate onSubmit={handleSubmit}>
                {/* Profile Name (optional) - full width */}
                <Styled.InputWrapper style={{ position: "relative" }}>
                  <Input
                    type="text"
                    name="username"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.username}
                    placeholder="Profile Name"
                    variant={"secondary"}
                    containerStyle={{ marginBottom: 0 }}
                    inputStyle={{ paddingRight: 40 }}
                    maxLength={30}
                  />

                  <div style={{ position: "absolute", right: 10, top: 10 }}>
                    <IyugaTooltip
                      tooltip={
                        "Choose a profile name! No spaces allowed. If you skip this, we’ll create one for you using your first and last name."
                      }
                      placement="right"
                      width={230}
                    >
                      <InfoOutlinedIcon
                        style={{ cursor: "pointer", color: "#BBBBBB" }}
                        fontSize="small"
                      />
                    </IyugaTooltip>
                  </div>

                  <Styled.Error>
                    {errors.username && touched.username && (
                      <>{errors.username}</>
                    )}
                    {usernameErrors.find(
                      (error) => error.username === values.username
                    ) && (
                      <>
                        <img
                          src={ASSETS.validation_error}
                          className="validation_error"
                          alt="validation error NP"
                        />
                        <>
                          {
                            usernameErrors.find(
                              (error) => error.username === values.username
                            )?.message
                          }
                        </>
                      </>
                    )}
                  </Styled.Error>
                </Styled.InputWrapper>

                {/* First and Last name on one line, equally divided */}
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <Styled.InputWrapper>
                      <Input
                        type="text"
                        name="firstName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.firstName}
                        placeholder="First Name*"
                        variant={"secondary"}
                        containerStyle={{ marginBottom: 0, width: "100%" }}
                      />
                      <Styled.Error>
                        {errors.firstName && touched.firstName && (
                          <>{errors.firstName}</>
                        )}
                      </Styled.Error>
                    </Styled.InputWrapper>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Styled.InputWrapper>
                      <Input
                        type="text"
                        name="lastName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.lastName}
                        placeholder="Last Name*"
                        variant={"secondary"}
                        containerStyle={{ marginBottom: 0, width: "100%" }}
                      />
                      <Styled.Error>
                        {errors.lastName && touched.lastName && (
                          <>{errors.lastName}</>
                        )}
                      </Styled.Error>
                    </Styled.InputWrapper>
                  </div>
                </div>
                <Styled.InputWrapper>
                  <Input
                    type="email"
                    name="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    placeholder="Email"
                    variant={"secondary"}
                    containerStyle={{ marginBottom: 0 }}
                    autoComplete="email"
                    autoFocus={false}
                  />
                  <Styled.Error>
                    {
                      <>
                        {errors.email && touched.email && errors.email && (
                          <img
                            src={ASSETS.validation_error}
                            className="validation_error"
                            alt="validation error NP"
                          />
                        )}
                        <>{errors.email && touched.email && errors.email}</>
                        {emailErrors.find(
                          (error) => error.email === values.email
                        ) && (
                          <>
                            <img
                              src={ASSETS.validation_error}
                              className="validation_error"
                              alt="validation error NP"
                            />
                            <>
                              {
                                emailErrors.find(
                                  (error) => error.email === values.email
                                )?.message
                              }
                            </>
                          </>
                        )}
                      </>
                    }
                  </Styled.Error>
                </Styled.InputWrapper>
                <CustomPhoneInput
                  type={"phone"}
                  name="phone"
                  onChange={(value) => {
                    setValues((prevValues) => ({
                      ...prevValues,
                      phone: value.phoneNumber,
                      phone_number: value.actualNumber,
                      country_code: value.countryCode,
                    }));
                  }}
                  onBlur={handleBlur}
                  value={values.phone}
                  noPadding={true}
                />
                <Styled.Error>
                  {
                    <>
                      {errors.phone && touched.phone && errors.phone && (
                        <img
                          src={ASSETS.validation_error}
                          className="validation_error"
                          alt="validation error NP"
                        />
                      )}
                      <>{errors.phone && touched.phone && errors.phone}</>
                    </>
                  }
                </Styled.Error>
                {/* Country & State dropdowns */}
                <Styled.InputWrapper style={{ marginBottom: 12 }}>
                  <TextField
                    select
                    label="Country"
                    variant="filled"
                    fullWidth
                    value={values.country || "US"}
                    onChange={(e) => {
                      setFieldValue("country", e.target.value);
                      const states = State.getStatesOfCountry(
                        e.target.value
                      ).map((s) => ({ value: s.isoCode, label: s.name }));
                      setFieldValue("_availableStates", states);
                      setFieldValue("state", "");
                    }}
                    style={{ background: "white" }}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: "14px",
                        height: "48px",
                        alignItems: "center",
                        borderRadius: "8px",
                        border: "1px solid #E1E1E1",
                        backgroundColor: "#FFFFFF",
                        color: "#565556",
                        fontFamily: "DM Sans",
                      },
                      inputProps: {
                        style: { paddingLeft: "9px", paddingTop: "10px" },
                      },
                    }}
                  >
                    {Country.getAllCountries().map((c) => (
                      <MenuItem key={c.isoCode} value={c.isoCode}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.country && touched.country && (
                    <Styled.Error>{errors.country}</Styled.Error>
                  )}
                </Styled.InputWrapper>

                <Styled.InputWrapper style={{ marginBottom: 12 }}>
                  <TextField
                    select
                    label="State/Province"
                    variant="filled"
                    fullWidth
                    value={values.state || ""}
                    onChange={(e) => setFieldValue("state", e.target.value)}
                    style={{ background: "white" }}
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: "14px",
                        height: "48px",
                        alignItems: "center",
                        borderRadius: "8px",
                        border: "1px solid #E1E1E1",
                        backgroundColor: "#FFFFFF",
                        color: "#565556",
                        fontFamily: "DM Sans",
                      },
                      inputProps: {
                        style: { paddingLeft: "9px", paddingTop: "10px" },
                      },
                    }}
                  >
                    {(
                      values._availableStates ||
                      State.getStatesOfCountry(values.country || "US")
                    ).map((s) => (
                      <MenuItem
                        key={s.isoCode || s.value}
                        value={s.isoCode || s.value}
                      >
                        {s.name || s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.state && touched.state && (
                    <Styled.Error>{errors.state}</Styled.Error>
                  )}
                </Styled.InputWrapper>

                <Styled.InputWrapper>
                  <Input
                    type={"password"}
                    name="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    placeholder="Password"
                    variant={"secondary"}
                    containerStyle={{ marginBottom: 0 }}
                    autoComplete="new-password"
                  />

                  <Styled.Error>
                    {
                      <>
                        {errors.password &&
                          touched.password &&
                          errors.password && (
                            <img
                              src={ASSETS.validation_error}
                              className="validation_error"
                              alt="validation error NP"
                            />
                          )}
                        <>
                          {errors.password &&
                            touched.password &&
                            errors.password}
                        </>
                      </>
                    }
                  </Styled.Error>
                </Styled.InputWrapper>

                <Styled.InputWrapper>
                  <Input
                    type={"password"}
                    name="confirmPassword"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.confirmPassword}
                    placeholder="Re-enter password"
                    variant={"secondary"}
                    containerStyle={{ marginBottom: 0 }}
                    autoComplete="off"
                  />
                  <Styled.Error>
                    {
                      <>
                        {errors.confirmPassword &&
                          touched.confirmPassword &&
                          errors.confirmPassword && (
                            <img
                              src={ASSETS.validation_error}
                              className="validation_error"
                              alt="validation error NP"
                            />
                          )}
                        <>
                          {errors.confirmPassword &&
                            touched.confirmPassword &&
                            errors.confirmPassword}
                        </>
                      </>
                    }
                  </Styled.Error>
                </Styled.InputWrapper>

                <Styled.InputWrapper>
                  <Styled.DatePickerWrapper>
                    <DatePicker
                      selected={values.dob}
                      onChange={(date) => setFieldValue("dob", date || null)}
                      onBlur={() => setTouched({ ...touched, dob: true })}
                      placeholderText="Date of Birth"
                      dateFormat="MM/dd/yyyy"
                      ref={datePickerRef}
                      minDate={new Date(1940, 0, 1)}
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      popperPlacement="top-start"
                    />
                    <img
                      src={CalendarIcon}
                      alt="calendar"
                      style={{ cursor: "pointer" }}
                      onClick={() => datePickerRef.current.setOpen(true)}
                    />
                  </Styled.DatePickerWrapper>
                  {errors.dob && touched.dob && (
                    <Styled.Error>{errors.dob}</Styled.Error>
                  )}
                </Styled.InputWrapper>

                <Styled.InputWrapper>
                  <Input
                    type="text"
                    name="referral_code"
                    onChange={(e) => {
                      handleChange(e);
                      handleReferralCodeChange(e.target.value);
                    }}
                    onBlur={handleBlur}
                    value={values.referral_code}
                    placeholder="Enter referral ID (optional)"
                    variant={"secondary"}
                    containerStyle={{ marginBottom: 0 }}
                    readOnly={!!referralData?.referral_code}
                    disabled={!!referralData?.referral_code}
                    endAdornment={
                      isReferralLoading && (
                        <img
                          src={ASSETS.loading}
                          alt="loading"
                          style={{ width: 20, height: 20 }}
                        />
                      )
                    }
                  />
                  {isReferralLoading && (
                    <TextView style={{ fontSize: 12, color: "#565556" }}>
                      Verifying referral code...
                    </TextView>
                  )}

                  {referralError && (
                    <TextView style={{ fontSize: 12, color: "red" }}>
                      {referralError}
                    </TextView>
                  )}

                  {referralProfile && (
                    <StyledSuccessText>
                      Referrer: {referralProfile.first_name}{" "}
                      {referralProfile.last_name}
                    </StyledSuccessText>
                  )}
                </Styled.InputWrapper>

                <div style={{ marginTop: 10 }}>
                  <FormControlLabel
                    control={
                      <StyledCheckbox
                        checked={values.termsAndConditions}
                        onChange={(e) =>
                          setFieldValue("termsAndConditions", e.target.checked)
                        }
                      />
                    }
                    label={
                      <Styled.TermsAndConditions>
                        <Styled.TermsAndConditionsText>
                          I agree with iYuga{" "}
                          <Styled.TermsAndConditionsLink
                            onClick={(e) => {
                              e.preventDefault();
                              setTermsPopup(true);
                            }}
                          >
                            Terms and Conditions
                          </Styled.TermsAndConditionsLink>
                        </Styled.TermsAndConditionsText>
                      </Styled.TermsAndConditions>
                    }
                  />
                  {errors.termsAndConditions && touched.termsAndConditions && (
                    <Styled.Error>{errors.termsAndConditions}</Styled.Error>
                  )}
                </div>

                <Button
                  style={{ marginTop: 20, marginBottom: 20 }}
                  type="submit"
                  isLoading={userLogin.isLoading}
                  disabled={
                    !isValid ||
                    emailErrors.some((error) => error.email === values.email) ||
                    usernameErrors.some(
                      (error) => error.username === values.username
                    )
                  }
                >
                  Sign Up
                </Button>

                {/* Invisible reCAPTCHA */}
                {RECAPTCHA_ENABLED && RECAPTCHA_SITE_KEY && (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    size="invisible"
                    onChange={onRecaptchaChange}
                    onExpired={onRecaptchaExpired}
                    onErrored={onRecaptchaError}
                    badge="bottomright"
                  />
                )}
                {RECAPTCHA_ENABLED && !RECAPTCHA_SITE_KEY && (
                  <div
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "10px",
                    }}
                  >
                    reCAPTCHA is not configured. Please add
                    REACT_APP_RECAPTCHA_SITE_KEY to your .env file.
                  </div>
                )}
              </form>
              <TextView style={{ textAlign: "center" }}>
                Already have an account?{" "}
                <span
                  onClick={() => nav("/login")}
                  style={{
                    fontWeight: "bold",
                    cursor: "pointer",
                    color: colors.PrimaryPurple,
                  }}
                >
                  Log In
                </span>
              </TextView>
              {/* <Styled.Separator>
                <Styled.Line />
                <TextView
                  style={{
                    padding: "0 10px",
                    position: "relative",
                    zIndex: 1,
                    width: 40,
                  }}
                >
                  OR
                </TextView>
                <Styled.Line />
              </Styled.Separator>

              <Styled.SocialBlock>
                <Styled.SocialItem onClick={loginWithGoogle}>
                  <img src={ASSETS.google2} />
                </Styled.SocialItem>

                <FacebookLogin
                  appId={"848410477295893"}
                  onSuccess={loginWithFacebook}
                  onFail={(error) => {
                    console.log("Login Failed!", error);
                  }}
                  render={({ onClick }) => (
                    <Styled.SocialItem onClick={onClick}>
                      <img src={ASSETS.fb} />
                    </Styled.SocialItem>
                  )}
                />

                <Styled.SocialItem>
                  <img src={ASSETS.apple} />
                </Styled.SocialItem>
              </Styled.SocialBlock> */}

              {/* <Styled.BottomView>
                  <Styled.Link href={PATH.privacy}>Privacy Policy</Styled.Link>
                  <Styled.Link href={PATH.terms}>Terms & Conditions</Styled.Link>
                </Styled.BottomView> */}

              {termsPopup && (
                <Dialog
                  fullWidth={true}
                  maxWidth={"md"}
                  open={termsPopup}
                  onClose={() => setTermsPopup(false)}
                >
                  <StyledDialogContent>
                    {/* <StyledDialogTitle>
                      <HeaderIcon src={ASSETS.Insurance} />
                      <TextView variant="title">
                        iYuga's Terms and Conditions and Privacy Policy
                      </TextView>
                    </StyledDialogTitle> */}

                    {termsLoading ? (
                      <div
                        style={{
                          margin: "0 auto",
                        }}
                      >
                        <CustomLoader />
                      </div>
                    ) : (
                      <StyledTermsContent>
                        <TermsAndConditionsAPI
                          data={termsData}
                          error={termsError}
                        />
                        {/* <TextView
                          variant="title"
                          style={{
                            marginTop: "16px",
                            marginBottom: "16px",
                            textAlign: "center",
                          }}
                        >
                          Privacy Policy
                        </TextView> */}
                        <PrivacyPolicyAPI data={termsData} error={termsError} />
                      </StyledTermsContent>
                    )}

                    <ButtonContainer>
                      <CancelButton onClick={() => setTermsPopup(false)}>
                        Cancel
                      </CancelButton>
                      <Button
                        onClick={() => {
                          setFieldValue("termsAndConditions", true);
                          setTermsPopup(false);
                        }}
                        style={{ width: "auto" }}
                      >
                        I Agree
                      </Button>
                    </ButtonContainer>
                  </StyledDialogContent>
                </Dialog>
              )}
              <EmailVerificationPopup
                open={showEmailVerificationPopup}
                onClose={() => setShowEmailVerificationPopup(false)}
                email={values.email}
              />
            </>
          )}
        </Formik>
      </>
      <BetaAmbassadorModal
        isOpen={passwordModalVisible}
        onClose={() => setPasswordModalVisible(false)}
        onAccess={() => setOpen(true)}
      />

      <OTPModal
        open={verifyModal}
        handleClose={() => {
          setVerifyModal(false);
        }}
        icon={Icon}
        title="Verify your email"
      >
        <Styled.ModalContent>
          <Styled.Message>
            {`Hi ${name}, Please verify your email address by clicking the link sent to`}
            <Styled.Message weight={900}>{email}</Styled.Message>
          </Styled.Message>
          <Separator />
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              onClick={handleResendVerification}
              isLoading={resendVerification.isLoading}
              style={{
                width: "158px",
                alignSelf: "center",
                background: "red",
              }}
            >
              Resend
            </Button>
            <Button
              onClick={() => {
                setVerifyModal(false);
                nav("/login");
              }}
              style={{
                width: "158px",
                alignSelf: "center",
              }}
            >
              Okay
            </Button>
          </Box>
        </Styled.ModalContent>
      </OTPModal>
    </AuthWrapper>
  );
};

export default Signup;
